import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { eliminarInforme as eliminarInformeApi, enviarInforme as enviarInformeApi, getClientes, getInformePdf, getInformesPendientes, getTodosLosInformes, regenerarInforme as regenerarInformeApi, revisarInforme as revisarInformeApi, verificarMantenimientoMes } from "../api";
import ModuleHeader from "./ui/ModuleHeader";
import "./InformesTab.css";

dayjs.locale("es");

function estadoInforme(info) {
  const revisado = !!info?.revisado;
  const enviado = !!info?.enviado;

  if (revisado && enviado) return { key: "enviado", label: "Enviado", badge: "badge-ok" };
  if (revisado) return { key: "revisado", label: "Revisado", badge: "badge-info" };
  return { key: "pendiente", label: "Pendiente", badge: "badge-warn" };
}

export default function InformesTab() {
  const [informes, setInformes] = useState([]);
  const [pendientes, setPendientes] = useState([]);

  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [clientesOptions, setClientesOptions] = useState([]);

  const [loading, setLoading] = useState({ informes: false, pendientes: false, clientes: false });
  const [busy, setBusy] = useState({}); // key -> true/false (acciones por item)
  const [err, setErr] = useState("");

  useEffect(() => {
    cargarTodo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarTodo = async () => {
    await Promise.all([cargarInformes(), cargarPendientes(), cargarClientes()]);
  };

  const cargarInformes = async () => {
    setLoading((p) => ({ ...p, informes: true }));
    setErr("");
    try {
      const res = await getTodosLosInformes();
      setInformes(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("❌ Error al cargar informes:", e);
      setErr(e?.response?.data?.error || "No se pudieron cargar los informes.");
      setInformes([]);
    } finally {
      setLoading((p) => ({ ...p, informes: false }));
    }
  };

  const cargarPendientes = async () => {
    setLoading((p) => ({ ...p, pendientes: true }));
    setErr("");
    try {
      const res = await getInformesPendientes();
      setPendientes(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("❌ Error al cargar pendientes:", e);
      setErr(e?.response?.data?.error || "No se pudieron cargar los informes pendientes.");
      setPendientes([]);
    } finally {
      setLoading((p) => ({ ...p, pendientes: false }));
    }
  };

  const cargarClientes = async () => {
    setLoading((p) => ({ ...p, clientes: true }));
    setErr("");
    try {
      const res = await getClientes();
      const list = Array.isArray(res.data) ? res.data : [];
      const opciones = list
        .map((c) => ({ value: c.nombre, label: c.nombre, id: c.id }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label)));
      setClientesOptions(opciones);
    } catch (e) {
      console.error("❌ Error al cargar clientes:", e);
      setErr(e?.response?.data?.error || "No se pudieron cargar los clientes.");
      setClientesOptions([]);
    } finally {
      setLoading((p) => ({ ...p, clientes: false }));
    }
  };

  const runBusy = async (key, fn) => {
    setBusy((p) => ({ ...p, [key]: true }));
    try {
      await fn();
    } finally {
      setBusy((p) => ({ ...p, [key]: false }));
    }
  };

  const eliminarInforme = async (numero) => {
    if (!window.confirm(`¿Eliminar el informe ${numero}?`)) return;
    await runBusy(`del:${numero}`, async () => {
      try {
        await eliminarInformeApi(numero);
        await Promise.all([cargarInformes(), cargarPendientes()]);
      } catch (e) {
        console.error("❌ Error al eliminar informe:", e);
        alert(e?.response?.data?.error || "Error al eliminar informe");
      }
    });
  };

  const revisarInforme = async (id) => {
    await runBusy(`rev:${id}`, async () => {
      try {
        await revisarInformeApi(id);
        await Promise.all([cargarInformes(), cargarPendientes()]);
      } catch (e) {
        console.error("❌ Error al revisar informe:", e);
        alert(e?.response?.data?.error || "Error al marcar como revisado.");
      }
    });
  };

  const enviarInforme = async (id) => {
    await runBusy(`send:${id}`, async () => {
      try {
        const res = await enviarInformeApi(id);
        alert(res?.data?.mensaje || "Informe enviado.");
        await Promise.all([cargarInformes(), cargarPendientes()]);
      } catch (e) {
        console.error("❌ Error al enviar informe:", e);
        alert(e?.response?.data?.error || "Error al enviar el informe.");
      }
    });
  };

  const regenerarInforme = async (numero) => {
    if (!window.confirm(`¿Regenerar el informe ${numero}?`)) return;
    await runBusy(`regen:${numero}`, async () => {
      try {
        const res = await regenerarInformeApi(numero);
        alert(res?.data?.mensaje || "Informe regenerado.");
        await Promise.all([cargarInformes(), cargarPendientes()]);
      } catch (e) {
        console.error("❌ Error al regenerar informe:", e);
        alert(e?.response?.data?.error || "Error al regenerar el informe.");
      }
    });
  };

  const abrirPdf = async (numero) => {
    await runBusy(`pdf:${numero}`, async () => {
      // Abrimos la pestaña durante el gesto del usuario para que el navegador
      // no la bloquee después de esperar la descarga autenticada.
      const tab = window.open("", "_blank");
      try {
        const res = await getInformePdf(numero);
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        if (tab) {
          tab.location.href = url;
        } else {
          const link = document.createElement("a");
          link.href = url;
          link.download = `informe_${numero}.pdf`;
          link.click();
        }
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (e) {
        if (tab && !tab.closed) tab.close();
        console.error("❌ Error abriendo PDF:", e);
        alert(e?.response?.data?.error || "No se pudo abrir el PDF del informe.");
      }
    });
  };

  const verificarInformeMesActual = async () => {
    if (!clienteSeleccionado?.id) return alert("Selecciona un cliente válido.");

    const mesActual = dayjs().format("YYYY-MM");
    await runBusy(`check:${clienteSeleccionado.id}`, async () => {
      try {
        const res = await verificarMantenimientoMes(clienteSeleccionado.id, mesActual);
        alert(res?.data?.mensaje || "Verificación realizada.");
        await Promise.all([cargarInformes(), cargarPendientes()]);
      } catch (e) {
        console.error("❌ Error verificando informe:", e);
        alert(e?.response?.data?.error || "Error al verificar el informe.");
      }
    });
  };

  const pendientesSorted = useMemo(() => {
    return [...(Array.isArray(pendientes) ? pendientes : [])].sort((a, b) => {
      // Mes desc (si viene YYYY-MM)
      return String(b?.mes || "").localeCompare(String(a?.mes || ""));
    });
  }, [pendientes]);

  const informesFiltrados = useMemo(() => {
    if (!clienteSeleccionado) return [];
    return (Array.isArray(informes) ? informes : []).filter((i) => i.cliente === clienteSeleccionado.value);
  }, [informes, clienteSeleccionado]);

  const selectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "rgba(255,255,255,0.03)",
      borderColor: "rgba(255,255,255,0.10)",
      boxShadow: "none",
      minHeight: 42,
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    singleValue: (base) => ({ ...base, color: "rgba(255,255,255,0.92)" }),
    input: (base) => ({ ...base, color: "rgba(255,255,255,0.92)" }),
    placeholder: (base) => ({ ...base, color: "rgba(255,255,255,0.55)" }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "rgba(255,255,255,0.06)" : "rgba(20,20,24,1)",
      color: "rgba(255,255,255,0.92)",
    }),
  };

  const isLoadingAny = loading.informes || loading.pendientes || loading.clientes;

  return (
    <div className="infWrap">
      <ModuleHeader
        eyebrow="Operación global"
        title="Informes"
        description="Revisión, envío y control de informes mensuales."
        actions={<button className="btn" type="button" onClick={cargarTodo} disabled={isLoadingAny}>{isLoadingAny ? "⏳ Cargando…" : "↻ Recargar"}</button>}
      />

      {err ? <div className="card infError">{err}</div> : null}

      {/* Pendientes */}
      <div className="card mp-section infCard">
        <div className="mp-section-head infCardHead">
          <div>
            <div className="infH">Pendientes de revisión</div>
            <div className="infSub2">
              {loading.pendientes ? "Cargando…" : `${pendientesSorted.length} pendientes`}
            </div>
          </div>
        </div>

        {pendientesSorted.length === 0 ? (
          <div className="infEmpty">✅ No hay informes pendientes.</div>
        ) : (
          <div className="infList">
            {pendientesSorted.map((p) => {
              const num = p.numero_informe;
              const revisado = p.revisado === 1;
              const hasPdf = Boolean(String(p.ruta_pdf || "").trim());

              return (
                <div key={num} className="infItem">
                  <div className="infItemLeft">
                    <div className="infItemTitle">
                      <span className="infCliente">{p.cliente}</span>
                      <span className="infChip">{p.mes}</span>
                      {revisado ? <span className="badge badge-ok">Revisado</span> : <span className="badge badge-warn">Pendiente</span>}
                    </div>

                    <div className="infItemMeta">
                      {hasPdf ? <button className="infLink" type="button" onClick={() => abrirPdf(num)} disabled={!!busy[`pdf:${num}`]}>📄 Ver PDF</button> : <span className="infWarn">⚠️ PDF pendiente de generar</span>}
                      <span className="infMuted">Nº {num}</span>
                      {p.email ? (
                        <span className="infMuted">📧 {p.email}</span>
                      ) : (
                        <span className="infWarn">⚠️ Sin email en la ficha</span>
                      )}
                    </div>
                  </div>

                  <div className="infItemActions">
                    <button
                      className="btn"
                      type="button"
                      onClick={() => regenerarInforme(num)}
                      disabled={!!busy[`regen:${num}`]}
                      title="Generar o regenerar el PDF con los mantenimientos del mes"
                    >
                      {busy[`regen:${num}`] ? "Generando…" : hasPdf ? "♻️ Regenerar PDF" : "📄 Generar PDF"}
                    </button>
                    {!revisado ? (
                      <button
                        className="btn"
                        type="button"
                        onClick={() => revisarInforme(num)}
                        disabled={!hasPdf || !!busy[`rev:${num}`]}
                        title={!hasPdf ? "Genera el PDF antes de revisar el informe" : "Marcar como revisado"}
                      >
                        {busy[`rev:${num}`] ? "Revisando…" : "✅ Revisar"}
                      </button>
                    ) : (
                      <button className="btn" type="button" disabled title="Ya revisado">
                        ✔ Revisado
                      </button>
                    )}

                    <button
                      className="btn"
                      type="button"
                      onClick={() => {
                        if (!p.email) return alert("⚠️ Este cliente no tiene email en su ficha.");
                        enviarInforme(num);
                      }}
                      disabled={!hasPdf || !revisado || !p.email || !!busy[`send:${num}`]}
                      title={!hasPdf ? "Genera el PDF antes de enviar" : !revisado ? "Revisa el informe antes de enviarlo" : !p.email ? "Sin email en la ficha" : "Enviar informe por email"}
                    >
                      {busy[`send:${num}`] ? "Enviando…" : "📧 Enviar"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Generados */}
      <div className="card mp-section infCard">
        <div className="mp-section-head infCardHead infCardHead--space">
          <div>
            <div className="infH">Informes generados</div>
            <div className="infSub2">Filtra por cliente para ver el histórico.</div>
          </div>

          <div className="infFilter">
            <Select
              styles={selectStyles}
              options={clientesOptions}
              isClearable
              isLoading={loading.clientes}
              placeholder="Selecciona un cliente…"
              onChange={setClienteSeleccionado}
              value={clienteSeleccionado}
            />
          </div>
        </div>

        {!clienteSeleccionado ? (
          <div className="infEmpty">Selecciona un cliente para ver sus informes.</div>
        ) : (
          <>
            <div className="infTools">
              <button
                className="btn"
                type="button"
                onClick={verificarInformeMesActual}
                disabled={!!busy[`check:${clienteSeleccionado.id}`]}
              >
                {busy[`check:${clienteSeleccionado.id}`] ? "Verificando…" : "🔍 Verificar informe de este mes"}
              </button>
            </div>

            {informesFiltrados.length === 0 ? (
              <div className="infEmpty infEmpty--warn">❌ No hay informes para este cliente.</div>
            ) : (
              <div className="infTableWrap">
                <table className="infTable">
                  <thead>
                    <tr>
                      <th style={{ width: 80 }}>Nº</th>
                      <th>Cliente</th>
                      <th style={{ width: 160 }}>Técnico</th>
                      <th style={{ width: 130 }}>Fecha</th>
                      <th style={{ width: 120 }}>Mes</th>
                      <th style={{ width: 130 }}>Estado</th>
                      <th style={{ width: 120 }}>PDF</th>
                      <th style={{ width: 170, textAlign: "right" }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {informesFiltrados.map((info) => {
                      const st = estadoInforme(info);
                      const num = info.numero_informe;

                      return (
                        <tr key={num}>
                          <td className="infMono">{num}</td>
                          <td>
                            <div className="infCellMain">{info.cliente}</div>
                            <div className="infCellSub">Mes: {info.mes}</div>
                          </td>
                          <td className="infMono">{info.tecnico || "—"}</td>
                          <td className="infMono">{info.fecha || "—"}</td>
                          <td>{dayjs(info.mes + "-01").format("MMMM")}</td>
                          <td>
                            <span className={`badge ${st.badge}`}>{st.label}</span>
                          </td>
                          <td>
                            <button className="infLink" type="button" onClick={() => abrirPdf(num)} disabled={!!busy[`pdf:${num}`]}>
                              {busy[`pdf:${num}`] ? "Abriendo…" : "📄 Ver"}
                            </button>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <div className="infRowActions">
                              <button
                                className="btn infBtnGhost"
                                type="button"
                                onClick={() => regenerarInforme(num)}
                                disabled={!!busy[`regen:${num}`]}
                                title="Regenerar informe"
                              >
                                {busy[`regen:${num}`] ? "…" : "♻️"}
                              </button>

                              <button
                                className="btn infBtnDanger"
                                type="button"
                                onClick={() => eliminarInforme(num)}
                                disabled={!!busy[`del:${num}`]}
                                title="Eliminar informe"
                              >
                                {busy[`del:${num}`] ? "…" : "🗑️"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
